import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { useI18n } from "../i18n";
import { api } from "../lib/api";

export default function Gateways() {
  const { t, lang } = useI18n();
  const [gateways, setGateways] = useState([]);
  useEffect(() => {
    api.get("/gateways").then((r) => setGateways(r.data)).catch(() => {});
  }, []);

  const featured = gateways.filter((g) => g.featured);
  const others = gateways.filter((g) => !g.featured);

  return (
    <>
      <PageHero label={t.gateways.label} title={t.gateways.heading} image="https://images.unsplash.com/photo-1759662280562-c16a625f5fa3?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000" alt="Belgrade" />
      <section className="bg-[#F7F5F0] py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal><p className="font-body text-[#2A2A2A]/75 text-xl md:text-2xl leading-relaxed max-w-3xl mb-20">{t.gateways.body}</p></Reveal>

          {featured.map((g) => (
            <Reveal key={g.id} className="mb-16">
              <div className="grid md:grid-cols-12 gap-10 items-center" data-testid={`gateway-${g.slug}`}>
                <div className="md:col-span-7 relative overflow-hidden group h-[50vh] md:h-[70vh]">
                  <img src={g.image} alt={g.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.4s]" />
                  <div className="absolute inset-0 bg-[#062029]/20" />
                  <span className="absolute top-6 left-6 label text-[#062029] bg-[#C5A880] px-4 py-2">{t.gateways.featured}</span>
                </div>
                <div className="md:col-span-5">
                  <h2 className="font-serif-display text-[#062029] text-5xl md:text-7xl">{g.name}</h2>
                  <p className="font-body text-[#C5A880] mt-4 tracking-wide uppercase text-xs">{lang === "de" ? g.tagline_de : g.tagline_en}</p>
                  <p className="font-body text-[#2A2A2A]/75 mt-8 text-lg leading-relaxed">{lang === "de" ? g.body_de : g.body_en}</p>
                </div>
              </div>
            </Reveal>
          ))}

          <div className="mt-24 border-t border-[#2A2A2A]/12 pt-12">
            <p className="label text-[#C5A880] mb-10">{t.gateways.upcoming}</p>
            <div className="grid md:grid-cols-3 gap-8">
              {others.map((g, i) => (
                <motion.div
                  key={g.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: i * 0.1 }}
                  data-testid={`gateway-${g.slug}`}
                  className="group relative overflow-hidden h-[42vh]"
                >
                  <img src={g.image} alt={g.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-1000" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#062029]/90 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-7">
                    <h3 className="font-serif-display text-[#F7F5F0] text-3xl">{g.name}</h3>
                    <p className="font-body text-[#F7F5F0]/70 text-sm mt-2">{lang === "de" ? g.tagline_de : g.tagline_en}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <Reveal>
            <Link to="/contact" data-testid="gateways-cta" className="inline-flex items-center gap-3 mt-20 border border-[#062029]/40 text-[#062029] px-8 py-4 text-[12px] tracking-widest uppercase hover:bg-[#062029] hover:text-[#F7F5F0] transition-colors duration-500">
              {t.nav.cta} <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
